import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Input, InputNumber, Segmented, Select, Modal } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../../config';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeResponsiveTable, NdoeDataCard,
  NdoeStatusBadge, NdoeImage, NdoeStats, NdoeStatCard, NdoeError,
  NdoeArticleLink, useFicheArticle,
} from '../../components/ndoe';
import useRefLists from '../rapports/useRefLists';
import './receptionArrivage.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const money = (v) => `${Number(v || 0).toLocaleString('fr-FR')} $`;
const dateTxt = (v) => (v ? new Date(v).toLocaleDateString('fr-FR') : '—');

// Écran « Coûts d'achat » : compléter les réceptions sans coût (ligne par ligne,
// audité) et lancer un RATTRAPAGE en masse — rejouable, avec aperçu obligatoire.
// Le rattrapage ne touche jamais un coût réel (préséance vérifiée côté serveur).
export default function CoutsAchat() {
  const user = useSelector((s) => s.user?.currentUser);
  const role = String(user?.role || '').toLowerCase();
  // Garde front indicative : le serveur applique le paramètre `cout_roles` (défaut admin).
  const allowed = role === 'admin';

  const [onglet, setOnglet] = useState('sans-cout');
  const { openFiche, ficheElement } = useFicheArticle();
  const refs = useRefLists(allowed);

  // --- Onglet 1 : réceptions sans coût --------------------------------------
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [payload, setPayload] = useState({ meta: {}, data: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saisies, setSaisies] = useState({});   // id_reception -> { cm, cf }
  const [busyLigne, setBusyLigne] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(q.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [q]);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/produit/receptions/sans-cout`, {
        params: { page, page_size: 20, q: search },
      });
      setPayload(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger les réceptions sans coût.');
    } finally { setLoading(false); }
  }, [page, search]);
  useEffect(() => { if (allowed) charger(); }, [allowed, charger]);

  const enregistrerLigne = async (r) => {
    const s = saisies[r.id_reception] || {};
    if (s.cm === null || s.cm === undefined) { toast.warn('Saisis au moins le coût marchandise.'); return; }
    setBusyLigne(r.id_reception);
    try {
      await axios.put(`${DOMAIN}/api/produit/reception/${r.id_reception}/cout`, {
        cout_marchandise: s.cm, cout_frais: s.cf ?? null, motif: 'Complété depuis « Coûts d\'achat »',
      });
      toast.success(`Coût enregistré — ${r.nom_produit} P${r.taille}.`);
      setSaisies((prev) => { const n = { ...prev }; delete n[r.id_reception]; return n; });
      charger();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Enregistrement impossible.');
    } finally { setBusyLigne(null); }
  };

  // --- Onglet 2 : rattrapage en masse ----------------------------------------
  const [mode, setMode] = useState('categorie');
  const [cible, setCible] = useState(null);
  const [methode, setMethode] = useState('taux');   // taux | cout
  const [taux, setTaux] = useState(40);
  const [cout, setCout] = useState(null);
  const [produits, setProduits] = useState([]);
  const [apercu, setApercu] = useState(null);
  const [busyRat, setBusyRat] = useState(false);

  useEffect(() => {
    if (!allowed || mode !== 'produit' || produits.length) return;
    axios.get(`${DOMAIN}/api/produit`)
      .then(({ data }) => setProduits((data || []).map((p) => ({ value: p.id_produit, label: p.nom_produit }))))
      .catch(() => {});
  }, [allowed, mode, produits.length]);

  useEffect(() => { setApercu(null); setCible(null); }, [mode]);
  useEffect(() => { setApercu(null); }, [cible, methode, taux, cout]);

  const corpsRattrapage = useMemo(() => ({
    mode,
    id_cible: mode === 'global' ? undefined : cible,
    taux: methode === 'taux' ? taux : undefined,
    cout: methode === 'cout' ? cout : undefined,
  }), [mode, cible, methode, taux, cout]);

  const pretPourApercu = mode === 'global' ? true : !!cible;

  const lancerApercu = async () => {
    setBusyRat(true);
    try {
      const { data } = await axios.post(`${DOMAIN}/api/produit/reception/cout/rattrapage`,
        { ...corpsRattrapage, dry_run: true });
      setApercu(data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Aperçu impossible.');
    } finally { setBusyRat(false); }
  };

  const appliquer = () => {
    Modal.confirm({
      title: 'Appliquer le rattrapage ?',
      content: `${apercu.modifiables} ligne(s) recevront un coût ; ${apercu.protegees} ligne(s) à coût réel sont protégées et ne seront pas touchées. Cette opération est rejouable.`,
      okText: 'Appliquer', cancelText: 'Annuler',
      onOk: async () => {
        try {
          const { data } = await axios.post(`${DOMAIN}/api/produit/reception/cout/rattrapage`,
            { ...corpsRattrapage, dry_run: false, motif: 'Rattrapage en masse' });
          toast.success(`${data.appliquees} ligne(s) valorisée(s) — ${data.protegees} protégée(s).`);
          setApercu(null);
          charger();
        } catch (e) {
          toast.error(e.response?.data?.error || 'Rattrapage impossible.');
        }
      },
    });
  };

  if (!allowed) {
    return (
      <NdoePage>
        <NdoeError title="Accès réservé"
          text="La gestion des coûts d'achat est réservée à l'administrateur (rôles ajustables via le paramètre cout_roles)." />
      </NdoePage>
    );
  }

  const { meta = {}, data = [] } = payload;

  const columns = [
    {
      title: 'Article', dataIndex: 'nom_produit',
      render: (nom, r) => (
        <div className="ca-article">
          <NdoeImage src={r.img} alt={nom} size="sm" preview={false} />
          <div>
            <NdoeArticleLink onClick={() => openFiche(r.id_produit, r.id_varianteProduit)}><b>{nom}</b></NdoeArticleLink>
            <small>P{r.taille}{r.couleur ? ` · ${r.couleur}` : ''} · reçu le {dateTxt(r.created_at)}</small>
          </div>
        </div>
      ),
    },
    { title: 'Qté', dataIndex: 'quantite', align: 'right', width: 70, render: (v) => <span className="num">{v}</span> },
    { title: 'Prix de vente', dataIndex: 'prix_vente', align: 'right', width: 116, render: (v) => <span className="num">{money(v)}</span> },
    {
      title: 'Coût marchandise', width: 150,
      render: (_, r) => (
        <InputNumber inputMode="decimal" min={0} placeholder="$" value={saisies[r.id_reception]?.cm}
          onChange={(v) => setSaisies((p) => ({ ...p, [r.id_reception]: { ...p[r.id_reception], cm: v } }))} />
      ),
    },
    {
      title: 'Frais', width: 110,
      render: (_, r) => (
        <InputNumber inputMode="decimal" min={0} placeholder="$" value={saisies[r.id_reception]?.cf}
          onChange={(v) => setSaisies((p) => ({ ...p, [r.id_reception]: { ...p[r.id_reception], cf: v } }))} />
      ),
    },
    {
      title: '', width: 130,
      render: (_, r) => (
        <NdoeActionButton size="sm" variant="secondary" loading={busyLigne === r.id_reception}
          onClick={() => enregistrerLigne(r)}>
          Enregistrer
        </NdoeActionButton>
      ),
    },
  ];

  const renderCard = (r) => (
    <NdoeDataCard
      key={r.id_reception}
      media={<NdoeImage src={r.img} alt={r.nom_produit} size="lg" preview={false} />}
      title={`${r.nom_produit} · P${r.taille}`}
      subtitle={`${r.quantite} paire(s) · reçu le ${dateTxt(r.created_at)} · vend ${money(r.prix_vente)}`}
      badge={<NdoeStatusBadge kind="custom" tone="warn" label="Sans coût" />}
      actions={(
        <div className="ca-card-form">
          <InputNumber inputMode="decimal" min={0} placeholder="Coût $" value={saisies[r.id_reception]?.cm}
            onChange={(v) => setSaisies((p) => ({ ...p, [r.id_reception]: { ...p[r.id_reception], cm: v } }))} />
          <InputNumber inputMode="decimal" min={0} placeholder="Frais $" value={saisies[r.id_reception]?.cf}
            onChange={(v) => setSaisies((p) => ({ ...p, [r.id_reception]: { ...p[r.id_reception], cf: v } }))} />
          <NdoeActionButton size="sm" variant="secondary" loading={busyLigne === r.id_reception}
            onClick={() => enregistrerLigne(r)}>OK</NdoeActionButton>
        </div>
      )}
    />
  );

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Réception"
        title="Coûts d'achat"
        subtitle="Compléter les réceptions sans coût, ou valoriser le stock historique en masse — sans jamais écraser un coût réel."
      />

      <Segmented value={onglet} onChange={setOnglet} style={{ marginBottom: 16 }}
        options={[
          { value: 'sans-cout', label: `Réceptions sans coût${meta.total ? ` (${meta.total})` : ''}` },
          { value: 'rattrapage', label: 'Rattrapage en masse' },
        ]} />

      {onglet === 'sans-cout' && (
        <>
          <div className="ca-toolbar">
            <Input allowClear prefix={<SearchOutlined />} placeholder="Rechercher un produit"
              value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 300 }} />
          </div>
          {error && !loading ? (
            <NdoeError text={error} onRetry={charger} />
          ) : (
            <NdoeResponsiveTable
              data={data}
              columns={columns}
              renderCard={renderCard}
              rowKey="id_reception"
              loading={loading}
              error={error}
              onRetry={charger}
              scroll={{ x: 900 }}
              pagination={{ current: meta.page || 1, pageSize: meta.page_size || 20, total: meta.total || 0, showSizeChanger: false }}
              onChange={(p) => setPage(p?.current || 1)}
              empty={{
                icon: '✅',
                title: 'Toutes les réceptions ont un coût',
                text: 'Rien à compléter — les nouveaux arrivages saisis avec facture arrivent déjà valorisés.',
              }}
            />
          )}
        </>
      )}

      {onglet === 'rattrapage' && (
        <div className="ca-rattrapage">
          <p className="ra-note" style={{ marginBottom: 14 }}>
            Le rattrapage pose un coût sur les réceptions <b>sans coût</b> ou déjà <b>rattrapées</b> —
            jamais sur un coût saisi ou réparti depuis une facture. Il est <b>rejouable</b> : relancer
            avec une meilleure valeur écrase le rattrapage précédent, pas les coûts réels.
          </p>

          <div className="ra-facture" style={{ alignItems: 'flex-end' }}>
            <div className="ra-champ">
              <label>Portée</label>
              <Select style={{ minWidth: 170 }} value={mode} onChange={setMode}
                options={[
                  { value: 'categorie', label: 'Une catégorie' },
                  { value: 'marque', label: 'Une marque' },
                  { value: 'produit', label: 'Un produit' },
                  { value: 'global', label: 'Toute la boutique' },
                ]} />
            </div>
            {mode !== 'global' && (
              <div className="ra-champ">
                <label>Cible</label>
                <Select showSearch optionFilterProp="label" style={{ minWidth: 220 }}
                  placeholder="Choisir…" value={cible} onChange={setCible}
                  options={mode === 'categorie' ? refs.categories : mode === 'marque' ? refs.marques : produits} />
              </div>
            )}
            <div className="ra-champ">
              <label>Méthode</label>
              <Select style={{ minWidth: 210 }} value={methode} onChange={setMethode}
                options={[
                  { value: 'taux', label: 'Taux de marge cible (%)' },
                  { value: 'cout', label: 'Coût fixe ($)' },
                ]} />
            </div>
            <div className="ra-champ">
              <label>{methode === 'taux' ? 'Taux (%)' : 'Coût ($)'}</label>
              {methode === 'taux'
                ? <InputNumber inputMode="decimal" min={1} max={500} value={taux} onChange={setTaux} />
                : <InputNumber inputMode="decimal" min={0} value={cout} onChange={setCout} />}
            </div>
            <NdoeActionButton variant="secondary" loading={busyRat} disabled={!pretPourApercu}
              onClick={lancerApercu}>
              Aperçu (sans rien modifier)
            </NdoeActionButton>
          </div>

          {methode === 'taux' && (
            <p className="ra-note">
              Coût estimé = prix de vente ÷ (1 + taux/100), calculé <b>ligne par ligne</b> sur le prix
              de chaque produit. Ex. taux 40 % sur un produit vendu 90 $ → coût 64,29 $.
            </p>
          )}

          {apercu && (
            <>
              <NdoeStats className="ra-stats">
                <NdoeStatCard label="Lignes qui recevront un coût" value={apercu.modifiables} gold />
                <NdoeStatCard label="Lignes protégées (coût réel)" value={apercu.protegees} />
              </NdoeStats>
              <div className="ra-submit" style={{ maxWidth: 420 }}>
                <NdoeActionButton variant="primary" disabled={!apercu.modifiables} onClick={appliquer}>
                  Appliquer le rattrapage ({apercu.modifiables} ligne{apercu.modifiables > 1 ? 's' : ''})
                </NdoeActionButton>
                {!apercu.modifiables && (
                  <small className="ra-submit__hint">Rien à valoriser sur cette portée : tout a déjà un coût réel.</small>
                )}
              </div>
            </>
          )}
        </div>
      )}
      {ficheElement}
    </NdoePage>
  );
}
